import React, { useEffect, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerResumenInicio, marcarRecordatorio } from '../services/metasService';
import { calcularAvance, coachDelDia, estadoPresupuesto, diasSinVender, fraseAnimo, mesDe, nombreMes, fechaYMD } from '../utils/metas';

const ACENTO = 'var(--color-primario,#DB2777)';
const ESTADO = {
  empezando: ['🌱 Empezando el mes', '#6D28D9', '#EDE9FE'],
  adelantada: ['🚀 Vas adelantada', '#047857', '#D1FAE5'],
  en_ritmo: ['👍 Vas en ritmo', '#0369A1', '#E0F2FE'],
  atrasada: ['⏰ Vas atrasada', '#B91C1C', '#FEE2E2'],
  cumplida: ['🏆 ¡Meta cumplida!', '#B45309', '#FEF3C7'],
};
const TONO = { bien: '#065F46', atencion: '#92400E', alerta: '#991B1B', idea: '#5B21B6' };

// 🎯 En Inicio: cómo va la meta del mes, qué toca hoy, ánimo y pendientes.
export default function MetaInicioCard({ onIrMetas }) {
  const [d, setD] = useState(null);
  const s = simboloMoneda();
  const dinero = (n) => `${s} ${Math.round(Number(n || 0)).toLocaleString('es-HN')}`;
  const hoy = new Date();
  const mes = mesDe(hoy);

  useEffect(() => {
    let activo = true;
    obtenerResumenInicio(mes).then((x) => activo && setD(x)).catch(() => activo && setD({ error: true }));
    return () => { activo = false; };
  }, [mes]);

  if (!d || d.error) return null;

  if (!d.meta) {
    return (
      <div className="k-card" style={{ padding: '18px', background: 'linear-gradient(135deg,#F5F3FF 0%,#FDF2F8 100%)', border: '1px solid #DDD6FE' }}>
        <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '38px' }}>🎯</span>
          <div style={{ flex: '1 1 240px' }}>
            <p style={{ margin: 0, fontWeight: 800, fontSize: '16px' }}>Ponte una meta para {nombreMes(mes).split(' ')[0]}</p>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>Define cuánto quieres vender, tu presupuesto y tu plan. Kontrol te acompaña cada día para lograrlo. 💜</p>
          </div>
          <button onClick={onIrMetas} style={{ padding: '10px 18px', borderRadius: '12px', border: 'none', background: 'linear-gradient(90deg,#6D28D9,#DB2777)', color: 'white', fontWeight: 800, cursor: 'pointer' }}>Planificar mi mes</button>
        </div>
      </div>
    );
  }

  const porFecha = {};
  let vendido = 0;
  (d.ventas || []).forEach((v) => {
    const f = fechaYMD(new Date(v.vendido_en)); const t = Number(v.total || 0);
    porFecha[f] = (porFecha[f] || 0) + t;
    if (f >= mes) vendido += t;
  });
  (d.libres || []).forEach((i) => {
    const f = String(i.fecha).slice(0, 10); const t = Number(i.monto || 0);
    porFecha[f] = (porFecha[f] || 0) + t;
    if (f >= mes) vendido += t;
  });
  const gastado = {};
  (d.gastos || []).forEach((g) => { gastado[g.categoria || 'Otros'] = (gastado[g.categoria || 'Otros'] || 0) + Number(g.monto || 0); });
  const avance = calcularAvance({ meta: d.meta.meta_ventas, vendido, mes, diasVenta: d.meta.dias_venta, hoy });
  const pendientes = (d.recordatorios || []).filter((r) => !r.hecho);
  const coach = coachDelDia({
    avance, presupuesto: estadoPresupuesto(d.meta.presupuesto || {}, gastado),
    diasSinVender: Object.keys(porFecha).length ? diasSinVender(porFecha, d.meta.dias_venta, hoy) : 0,
  }, s).slice(1, 3); // el primero (cómo vas) ya se ve arriba
  const [txt, color, fondo] = ESTADO[avance.estado];

  const hecho = async (r) => {
    setD((x) => ({ ...x, recordatorios: x.recordatorios.map((y) => (y.id === r.id ? { ...y, hecho: true } : y)) }));
    await marcarRecordatorio(r.id, true).catch(() => {});
  };

  return (
    <div className="k-card" style={{ padding: '18px', background: 'linear-gradient(135deg,#FDF2F8 0%,#F5F3FF 100%)', border: '1px solid #EEE9F8' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <p className="k-sec-title" style={{ margin: 0 }}>🎯 Mi meta de {nombreMes(mes).split(' ')[0]}</p>
        <span style={{ padding: '4px 10px', borderRadius: '999px', fontWeight: 800, fontSize: '12px', color, background: fondo }}>{txt}</span>
      </div>
      <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
        Llevas <b style={{ fontSize: '20px', color: '#1F1A2E' }}>{dinero(avance.vendido)}</b> de {dinero(avance.meta)} · <b>{avance.pct}%</b>
      </p>
      <div style={{ position: 'relative', height: '12px', background: 'white', borderRadius: '999px', margin: '8px 0', overflow: 'hidden' }} role="img" aria-label={`${avance.pct}% de la meta`}>
        <div style={{ width: `${Math.min(100, avance.pct)}%`, height: '100%', background: 'linear-gradient(90deg,#6D28D9,#DB2777)', borderRadius: '999px', transition: 'width .8s ease' }} />
        {avance.meta > 0 && <div title="Lo que deberías llevar hoy" style={{ position: 'absolute', top: 0, bottom: 0, left: `${Math.min(100, (avance.esperado / avance.meta) * 100)}%`, width: '2px', background: '#1F1A2E' }} />}
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <span style={{ background: 'white', padding: '6px 10px', borderRadius: '10px', fontSize: '13px' }}>
          🗓️ Hoy te toca: <b>{avance.estado === 'cumplida' ? '¡ya cumpliste!' : dinero(avance.porDiaRestante)}</b>
        </span>
        <span style={{ background: 'white', padding: '6px 10px', borderRadius: '10px', fontSize: '13px' }}>🎯 Faltan <b>{dinero(avance.falta)}</b> en {avance.restantes} días</span>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: 700, color: '#5B21B6' }}>💜 {fraseAnimo(avance.estado, hoy)}</p>
      {coach.map((c, i) => <p key={i} style={{ margin: '2px 0', fontSize: '13px', color: TONO[c.tono] }}>{c.icono} {c.texto}</p>)}
      {pendientes.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '8px 12px', marginTop: '8px' }}>
          <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 800 }}>⏰ Pendientes de hoy</p>
          {pendientes.slice(0, 4).map((r) => (
            <label key={r.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', padding: '3px 0', cursor: 'pointer' }}>
              <input type="checkbox" onChange={() => hecho(r)} style={{ width: '17px', height: '17px', accentColor: '#059669' }} />
              <span>{r.titulo} <span style={{ color: new Date(r.cuando) < hoy ? '#B91C1C' : '#9CA3AF' }}>· {new Date(r.cuando).toLocaleTimeString('es-HN', { hour: 'numeric', minute: '2-digit' })}</span></span>
            </label>
          ))}
        </div>
      )}
      <button onClick={onIrMetas} style={{ marginTop: '10px', padding: '8px 14px', borderRadius: '10px', border: `1px solid ${ACENTO}`, background: 'white', color: ACENTO, fontWeight: 700, cursor: 'pointer' }}>📅 ¿Qué hago hoy? · Ver mi plan</button>
    </div>
  );
}
